import ApartmentIcon from "@mui/icons-material/Apartment";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { Box, Button, FormHelperText, Grid2, Typography } from "@mui/material";
import { FormikProps } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { zoullooColors } from "../../../../../theme";
import { FreelancerOnboardingMyProfessionalInformationStepViewModel } from "../../../../../ViewModels/FreelancerOnboardingMyProfessionalInformationStepViewModel";

interface StatusSelectionSectionProps {
	formik: FormikProps<FreelancerOnboardingMyProfessionalInformationStepViewModel>;
	onStatusChange: (newStatus: "portage" | "structure" | null) => void;
	buttonRef?: React.RefObject<HTMLButtonElement>;
}

// Helper function to create status selection button styles
const getStatusButtonStyles = (isSelected: boolean) => ({
	color: zoullooColors.grey[700],
	borderColor: isSelected ? zoullooColors.secondary?.main : zoullooColors.grey[400],
	borderWidth: isSelected ? 2 : 1,
	"&:hover": {
		borderColor: isSelected ? zoullooColors.secondary?.main : zoullooColors.grey[700],
		backgroundColor: zoullooColors.grey[100]
	}
});

const StatusSelectionSection: React.FC<StatusSelectionSectionProps> = ({
	formik,
	onStatusChange,
	buttonRef
}) => {
	const { t } = useTranslation();

	const handleStatusClick = (status: "portage" | "structure") => {
		const newStatus = formik.values.selectedStatus === status ? null : status;
		onStatusChange(newStatus);
	};

	return (
		<Box sx={{ mb: 4 }}>
			<Typography variant="h4" sx={{ mb: 1 }}>
				{t("freelancerOnboardingMyProfessionalInformation.status.title")}
			</Typography>
			<Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
				{t("freelancerOnboardingMyProfessionalInformation.status.description")}
			</Typography>

			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12, md: 3 }}>
					<Button
						ref={buttonRef}
						startIcon={<AssignmentOutlinedIcon />}
						onClick={() => handleStatusClick("portage")}
						variant="outlined"
						size="large"
						disableRipple
						fullWidth
						sx={getStatusButtonStyles(formik.values.selectedStatus === "portage")}
					>
						{t("freelancerOnboardingMyProfessionalInformation.status.portageButton")}
					</Button>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 3 }}>
					<Button
						startIcon={<ApartmentIcon />}
						onClick={() => handleStatusClick("structure")}
						variant="outlined"
						size="large"
						disableRipple
						fullWidth
						sx={getStatusButtonStyles(formik.values.selectedStatus === "structure")}
					>
						{t("freelancerOnboardingMyProfessionalInformation.status.structureButton")}
					</Button>
				</Grid2>
			</Grid2>

			{/* Display validation error for status selection */}
			{formik.touched.selectedStatus && formik.errors.selectedStatus && (
				<FormHelperText error sx={{ mt: 1, fontSize: "1rem" }}>
					{formik.errors.selectedStatus}
				</FormHelperText>
			)}
		</Box>
	);
};

export default StatusSelectionSection;
